import {
  Phone,
  Mail,
  FileText,
  MessageSquare,
  Users,
  MapPin,
  Clock,
  Tag,
  User,
  FolderOpen,
  Scale,
  Smartphone
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { interactionTypeLabels as typeLabels } from '@/components/interactions/InteractionBadges'
import { InteractionActionsMenu } from './InteractionActionsMenu'
import { useDateTimeFormat } from '../hooks/useDateTimeFormat'
import { parseDbTimestamp } from '../utils/datetime'

type InteractionType =
  | 'meeting' | 'phone_call' | 'email' | 'whatsapp' | 'consultation'
  | 'document_review' | 'follow_up' | 'initial_contact'
  | 'court_appearance' | 'negotiation' | 'other'

interface Interaction {
  id: string
  type: InteractionType
  subject: string
  description?: string
  createdAt: string
  completedAt?: string
  tags: string[]
  createdBy: string
  linkedCaseId?: string
  linkedCaseNumber?: string
  linkedProcessId?: string
  linkedProcessNumber?: string
}

interface InteractionTimelineCardProps {
  interaction: Interaction
  onEdit?: () => void
  onDelete?: () => void
  onLink?: () => void
  onTransformToCase?: () => void
  onTransformToProcess?: () => void
  onPrint?: () => void
  onAttachDocument?: () => void
  onCreateTask?: () => void
  onDuplicate?: () => void
}

export function InteractionTimelineCard({
  interaction,
  onEdit,
  onDelete,
  onLink,
  onTransformToCase,
  onTransformToProcess,
  onPrint,
  onAttachDocument,
  onCreateTask,
  onDuplicate
}: InteractionTimelineCardProps) {
  const { formatDateOnly } = useDateTimeFormat()

  const formatTimeOnly = (dateString: string) => {
    const date = parseDbTimestamp(dateString)
    if (!date) return '-'
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  }

  const getInteractionIcon = (type: InteractionType) => {
    const icons: Record<InteractionType, typeof MessageSquare> = {
      meeting: Users,
      phone_call: Phone,
      email: Mail,
      whatsapp: Smartphone,
      document_review: FileText,
      consultation: MessageSquare,
      follow_up: Clock,
      initial_contact: MessageSquare,
      court_appearance: MapPin,
      negotiation: Users,
      other: MessageSquare
    }
    return icons[type] ?? MessageSquare
  }

  const IconComponent = getInteractionIcon(interaction.type)
  const displayDate = interaction.completedAt || interaction.createdAt

  return (
    <div className="flex gap-3 pb-6 sm:gap-4">
      {/* Marcador da linha do tempo */}
      <div className="flex flex-col items-center">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-full border bg-muted text-muted-foreground">
          <IconComponent className="size-5" aria-hidden="true" />
        </div>
        <div className="mt-2 w-px flex-1 bg-border" />
      </div>

      <div className="min-w-0 flex-1 rounded-lg border bg-card p-4 transition-shadow hover:shadow-sm">
        <div className="mb-2 flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
              <span className="font-medium">{typeLabels[interaction.type]}</span>
              <span aria-hidden="true">•</span>
              <span>{formatDateOnly(displayDate)} às {formatTimeOnly(displayDate)}</span>
            </div>
            {interaction.createdBy && (
              <div className="mb-1 flex items-center gap-1 text-xs text-muted-foreground">
                <User className="size-3 shrink-0" aria-hidden="true" />
                <span className="truncate">Registrado por: {interaction.createdBy}</span>
              </div>
            )}
            {(interaction.linkedCaseId || interaction.linkedProcessId) && (
              <div className="mb-2 flex flex-wrap items-center gap-2">
                {interaction.linkedCaseId && (
                  <Badge variant="outline">
                    <FolderOpen aria-hidden="true" />
                    Caso #{interaction.linkedCaseNumber || interaction.linkedCaseId}
                  </Badge>
                )}
                {interaction.linkedProcessId && (
                  <Badge variant="outline">
                    <Scale aria-hidden="true" />
                    Processo #{interaction.linkedProcessNumber || interaction.linkedProcessId}
                  </Badge>
                )}
              </div>
            )}
            <h4 className="mb-2 break-words text-base font-semibold">{interaction.subject}</h4>
          </div>
          <InteractionActionsMenu
            interactionId={interaction.id}
            onEdit={onEdit}
            onDelete={onDelete}
            onLink={onLink}
            onTransformToCase={onTransformToCase}
            onTransformToProcess={onTransformToProcess}
            onPrint={onPrint}
            onAttachDocument={onAttachDocument}
            onCreateTask={onCreateTask}
            onDuplicate={onDuplicate}
          />
        </div>

        {interaction.description && (
          <p className="mb-3 whitespace-pre-wrap break-words text-sm">{interaction.description}</p>
        )}

        {interaction.tags && interaction.tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {interaction.tags.map((tag, index) => (
              <Badge key={index} variant="secondary">
                <Tag aria-hidden="true" />
                {tag}
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
